import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LoadingState, EmptyState, EventTime } from "@/components/Common";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { RegistraContattoDialog } from "@/components/RegistraContattoDialog";
import { TaskDialog, PianificaStepDialog, TaskCompletableList, GestioneServiziDialog } from "@/components/CaseTaskDialogs";
import { CustomFieldsForm, hasCustomFields } from "@/components/CustomFields";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DictationTextarea } from "@/components/DictationTextarea";
import { CASE_STATUSES, PRIORITIES } from "@/lib/constants";
import { personName, fmtDate } from "@/lib/format";
import { ArrowLeft, Sparkles, Loader2, Download, PhoneCall, PlusCircle, User, FileText, CalendarClock, CalendarPlus, Save, Briefcase } from "lucide-react";
import { toast } from "sonner";

// Data odierna (locale) in formato YYYY-MM-DD per gli input date
const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [ai, setAi] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [note, setNote] = useState("");
  const [services, setServices] = useState([]);
  const [extra, setExtra] = useState({});
  const [savingExtra, setSavingExtra] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const { data } = await api.get(`/cases/${id}`); setData(data); setExtra(data?.pratica?.campi_extra || {}); }
    catch (e) { toast.error(extractError(e, "Pratica non accessibile")); }
    finally { if (!silent) setLoading(false); }
  }, [id]);
  useEffect(() => {
    load();
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, [load]);

  const update = async (patch) => {
    try { await api.put(`/cases/${id}`, patch); toast.success("Pratica aggiornata"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  // Cambio stato: se "Appuntamento fissato" proponi la pianificazione del prossimo step
  const changeStato = async (v) => {
    await update({ stato: v });
    if (v === "Appuntamento fissato") setStepOpen(true);
  };

  const saveExtra = async () => {
    setSavingExtra(true);
    try { await api.put(`/cases/${id}`, { campi_extra: extra }); toast.success("Campi aggiornati"); load(); }
    catch (e) { toast.error(extractError(e)); } finally { setSavingExtra(false); }
  };

  const genAi = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/ai/case/${id}/summary`); setAi(data.summary); }
    catch (e) { toast.error(extractError(e)); } finally { setAiLoading(false); }
  };

  const addNote = async (overrideText) => {
    const body = ((typeof overrideText === "string" ? overrideText : note) || "").trim();
    if (!body) return;
    try {
      await api.post("/interactions", { case_id: id, person_id: data.persona?.id, tipo: "Nota interna", oggetto: "Nota", descrizione: body });
      setNote(""); toast.success("Nota aggiunta"); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/cases/${id}/pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank");
    } catch (e) { toast.error(extractError(e)); }
  };

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState title="Pratica non trovata o non autorizzata" />;
  const c = data.pratica; const p = data.persona;
  const svc = services.find((s) => s.id === c.servizio_id);
  const defs = svc?.campi_personalizzati || [];
  const followupDefault = svc?.tipo_follow_up || "Appuntamento";

  return (
    <div>
      <button onClick={() => navigate("/app/pratiche")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-slate-900 mb-3"><ArrowLeft className="h-4 w-4" /> Pratiche</button>
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-semibold">{c.numero}</h1>
              <StatusBadge stato={c.stato} /><PriorityBadge priorita={c.priorita} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">{c.servizio_nome} · Aperta il {fmtDate(c.created_at)} · Origine: {c.origine}</p>
            {p && <button onClick={() => navigate(`/app/persone/${p.id}`)} className="inline-flex items-center gap-1 text-sm text-[color:var(--brand-blue-600)] mt-1 hover:underline"><User className="h-3.5 w-3.5" /> {personName(p)} · {p.telefono_principale}</button>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="case-pdf-button"><Download className="h-4 w-4 mr-1" /> PDF</Button>
            {can("interactions.write") && p && <RegistraContattoDialog defaultPersonId={p.id} onDone={load} trigger={<Button size="sm" className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><PhoneCall className="h-4 w-4 mr-1" /> Registra contatto</Button>} />}
            {can("tasks.write") && <Button size="sm" variant="secondary" onClick={() => setTaskOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Attività</Button>}
          </div>
        </div>
        <p className="mt-4 text-sm whitespace-pre-line">{c.descrizione}</p>
      </Card>

      {can("cases.write") && (
        <Card className="p-4 mt-4">
          <h3 className="font-semibold mb-3">Gestione pratica</h3>
          <div className="mb-4 rounded-lg border bg-[color:var(--brand-green-100)] px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="case-inizio-relazione">
            <div className="flex items-center gap-2 text-sm">
              <CalendarClock className="h-4 w-4 text-[color:var(--brand-green-700)] shrink-0" />
              <span className="font-medium">Inizio della relazione</span>
            </div>
            <Input type="date" max={todayISO()}
              value={(c.data_inizio_relazione || c.created_at || "").slice(0, 10)}
              onChange={(e) => e.target.value && update({ data_inizio_relazione: e.target.value })}
              className="h-9 w-auto bg-white sm:ml-2" data-testid="case-inizio-relazione-input" />
            <span className="text-[11px] text-muted-foreground sm:ml-auto">Modificabile: indica quando è iniziata la relazione (non futura).</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Stato</Label>
              <Select value={c.stato} onValueChange={changeStato}>
                <SelectTrigger data-testid="case-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CASE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Priorità</Label>
              <Select value={c.priorita} onValueChange={(v) => update({ priorita: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            {can("cases.assign") && <div><Label>Operatore assegnato</Label>
              <Select value={c.operatore_id || "nessuno"} onValueChange={(v) => update({ operatore_id: v === "nessuno" ? null : v })}>
                <SelectTrigger data-testid="case-assign-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent className="max-h-64"><SelectItem value="nessuno">Nessuno</SelectItem>{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome_completo}</SelectItem>)}</SelectContent>
              </Select></div>}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={() => setStepOpen(true)} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="pianifica-step-button">
              <CalendarPlus className="h-4 w-4 mr-2" /> Pianifica prossimo step
            </Button>
            <Button onClick={() => setSvcOpen(true)} className="bg-[color:var(--brand-green-700)] hover:bg-[color:var(--brand-green-600)] text-white" data-testid="case-gestione-servizi-button">
              <Briefcase className="h-4 w-4 mr-2" /> Gestione servizi
            </Button>
            <span className="text-[11px] text-muted-foreground">Fissa il prossimo step a calendario oppure registra il servizio erogato.</span>
          </div>

          {hasCustomFields(defs, "pratica") && (
            <div className="mt-5 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm">Campi aggiuntivi del servizio</h4>
                <Button size="sm" variant="outline" onClick={saveExtra} disabled={savingExtra} data-testid="save-case-extra-button">
                  {savingExtra ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva campi
                </Button>
              </div>
              <CustomFieldsForm defs={defs} values={extra} onChange={setExtra} target="pratica" />
            </div>
          )}
        </Card>
      )}

      <Tabs defaultValue="timeline" className="mt-4">
        <TabsList className="flex-wrap h-auto"><TabsTrigger value="timeline">Cronologia</TabsTrigger><TabsTrigger value="attivita">Attività ({data.attivita.length})</TabsTrigger><TabsTrigger value="documenti">Documenti</TabsTrigger><TabsTrigger value="ai">Riassunto AI</TabsTrigger></TabsList>

        <TabsContent value="timeline" className="mt-4">
          {can("interactions.write") && (
            <Card className="p-3 mb-3">
              <Label>Aggiungi nota interna</Label>
              <div className="flex gap-2 mt-1"><DictationTextarea value={note} onValueChange={setNote} onVoiceSave={addNote} rows={2} placeholder="Scrivi una nota o usa il microfono (di' «salva» per registrare)…" data-testid="case-note-input" />
                <Button onClick={addNote} className="self-end" data-testid="case-note-button">Aggiungi</Button></div>
            </Card>
          )}
          {data.timeline.length === 0 ? <EmptyState title="Nessun evento" /> : (
            <div className="space-y-0">{data.timeline.map((i) => (
              <div key={i.id} className="relative pl-10 pb-5 timeline-line">
                <div className="absolute left-2 top-1 h-6 w-6 rounded-full bg-[color:var(--brand-blue-100)] border-2 border-white flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-[color:var(--brand-blue-600)]" /></div>
                <div className="rounded-lg border bg-white p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap"><span className="text-sm font-medium">{i.tipo}</span><EventTime dataEvento={i.data_evento} createdAt={i.created_at} /></div>
                  {i.descrizione && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{i.descrizione}</div>}
                  <div className="text-[11px] text-muted-foreground mt-1">{i.operatore_nome}</div>
                </div>
              </div>
            ))}</div>
          )}
        </TabsContent>

        <TabsContent value="attivita" className="mt-4">
          {can("tasks.write") && (
            <div className="mb-3 flex justify-end">
              <Button size="sm" variant="secondary" onClick={() => setTaskOpen(true)} data-testid="case-add-task-button">
                <PlusCircle className="h-4 w-4 mr-1" /> Nuova attività
              </Button>
            </div>
          )}
          <TaskCompletableList tasks={data.attivita} canWrite={can("tasks.write")} onChanged={() => load(true)} />
        </TabsContent>

        <TabsContent value="documenti" className="mt-4">
          {data.documenti.length === 0 ? <EmptyState title="Nessun documento" /> : (
            <Card className="divide-y">{data.documenti.map((d) => (
              <div key={d.id} className="p-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><div><div className="text-sm font-medium">{d.titolo}</div><div className="text-xs text-muted-foreground">{d.categoria} · {fmtDate(d.created_at)}</div></div></div>
                <Button variant="ghost" size="sm" onClick={() => api.get(`/documents/${d.id}/download`, { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = d.nome_file || "documento"; a.click(); })}><Download className="h-4 w-4" /></Button>
              </div>
            ))}</Card>
          )}
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--brand-orange-600)]" /> Riassunto AI della pratica</h3>
              <Button size="sm" onClick={genAi} disabled={aiLoading} data-testid="ai-case-summary-button">{aiLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Genera</Button>
            </div>
            {ai ? <div className="whitespace-pre-line text-sm text-slate-700">{ai}<p className="text-[11px] text-muted-foreground mt-3">Contenuto generato dall'assistente AI. Verifica sempre le informazioni.</p></div>
              : <p className="text-sm text-muted-foreground">Premi "Genera" per un riassunto operativo della pratica.</p>}
          </Card>
        </TabsContent>
      </Tabs>
      <TaskDialog open={taskOpen} setOpen={setTaskOpen} caseId={id} personId={p?.id} onDone={load} />
      <PianificaStepDialog open={stepOpen} setOpen={setStepOpen} caseData={data} defaultTipo={followupDefault} onDone={load} />
      <GestioneServiziDialog open={svcOpen} setOpen={setSvcOpen} caseId={id} defaultServizioId={c.servizio_id} numero={c.numero} onDone={load} />
    </div>
  );
};

export default CaseDetail;
